import { useEffect, useState } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { getCapabilities } from './audit-api'

// What this caller may do with the audit trail (GET /capabilities): the UI
// never infers it from roles. Cached per login + organization +
// impersonation, so switching organization or starting or ending an
// impersonation reads it again. Everything is closed while loading and
// after a failed read. A flag the server doesn't send is false
// (signOutSessions: content adds it; until then the button stays hidden).
// impersonating comes from the token, not the server: auth refuses every
// destructive call made while impersonating, so the UI doesn't offer them.
// activityLog (ORG_ACTIVITY_LOG without the audit log, owner ruling
// 2026-10-04): the organization's log, Activity and Insights at the
// learning-activity level -- no IP or location (network false), no export,
// no record History unless the reader edits the record. canAudit covers it.
const NONE = {
  orgAuditLog: false,
  sysAuditLog: false,
  activityLog: false,
  network: false,
  requestLog: false,
  retention: false,
  table: false,
  export: false,
  signOutSessions: false
}
const cache = new Map()

export const resetAuditCapabilities = () => cache.clear()

const shape = (c, loaded, impersonating) => {
  const v = { ...NONE }
  Object.keys(NONE).forEach(k => {
    v[k] = Boolean(c && c[k])
  })
  return { ...v, loaded, canAudit: v.orgAuditLog || v.sysAuditLog || v.activityLog, impersonating }
}

export const isImpersonating = state => Boolean(state.jwtClaims && state.jwtClaims.impersonationEndsAt)

export const identityKey = state => `${state.loginID}:${state.organizationID}:${isImpersonating(state) ? 'i' : ''}`

export const useAuditCapabilities = () => {
  const { state } = useAuthContext()
  const key = identityKey(state)
  const signedIn = Boolean(state.loginID)
  const impersonating = isImpersonating(state)
  const cached = cache.get(key)
  const [caps, setCaps] = useState(() => shape(cached && cached.value, Boolean(cached && cached.value), impersonating))

  useEffect(() => {
    if (!signedIn) return undefined
    let live = true
    if (!cache.has(key))
      cache.set(key, {
        promise: getCapabilities().then(
          v => v || NONE,
          () => NONE
        )
      })
    const entry = cache.get(key)
    entry.promise.then(v => {
      entry.value = v
      if (live) setCaps(shape(v, true, impersonating))
    })
    return () => {
      live = false
    }
  }, [key, signedIn, impersonating])

  return caps
}
